import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { PlusOutlined } from '@ant-design/icons';
import {
  AdditionalFileModal,
  AuthorizedButton,
  Container,
  DataTable,
  AbroadInsuranceModal,
  PageTitle,
  SRangePicker,
  SExportButton,
} from '../../../components';
import { tableActions } from '../../../store/reducers/tableReducer';
import styles from './index.module.scss';
import globalStyles from '../../../assets/styles/index.module.scss';
import { INITIAL_VALUES } from './const';
import { useAppDispatch, useAppSelector } from '../../../store';
import useHttpService from '../../../services/hook';
import { getColumns } from './columns';
import validationSchema from './validations';
import { IExpandedRow } from '../../../components/DataTable/type';
import { IEnum } from '../../../services/type';
import {
  IFile,
  IWithViewPerm,
  IAbroadInsuranceDataSource,
  IAbroadInsuranceInitialValues,
} from '../../../types';
import { Checkbox, Flex, Typography } from 'antd';
import { modalTypes } from '../../../assets/enums';
import { downloadFile } from '../../../helpers/File';
import { cleanupAfterSubmit, prepareModalData } from '../../../utils/functions';
import useFetchParameters from '../../../hooks';
import AbroadInsuranceService from '../../../api-clients/AbroadInsuranceService';
import EnumService from '../../../api-clients/EnumService';
import AuthService from '../../../api-clients/AuthService';

const AbroadInsurance: FC = () => {
  const dispatch = useAppDispatch();
  const executeService = useHttpService();
  const organizations = useAppSelector(
    (state) => state.organizationReducer.organizations,
  );

  // Redux State
  const search = useAppSelector((state) => state.tableReducer.search);
  const sort = useAppSelector((state) => state.tableReducer.sort);
  const page = useAppSelector((state) => state.tableReducer.page);

  // State
  const [selectedItem, setSelectedItem] = useState<
    IAbroadInsuranceDataSource | undefined
  >(undefined);
  const [additionalFile, setAdditionalFile] = useState<IFile[] | undefined>(
    undefined,
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<IAbroadInsuranceDataSource[]>(
    [],
  );
  const [dates, setDates] = useState<string[]>([]);
  const [expandedRows, setExpandedRows] = useState<IExpandedRow[]>([]);
  const [branches, setBranches] = useState<IEnum[]>([]);
  const [countries, setCountries] = useState<IEnum[]>([]);
  const [statusChoices, setStatusChoices] = useState<IEnum[]>([]);
  const [additionalFileID, setAdditionalFileID] = useState<number | undefined>(
    undefined,
  );
  const [tempAdditionalFiles, setTempAdditionalFiles] = useState<
    FileList | undefined
  >(undefined);
  const [isExpired, setIsExpired] = useState<boolean | undefined>(undefined);
  const [withViewPerm, setWithViewPerm] = useState<IWithViewPerm[]>([]);

  const parameters = useFetchParameters({
    search,
    sort,
    page,
    dates,
    isExpired,
  });
  const abroadInsuranceService = useMemo(
    () => new AbroadInsuranceService(executeService, parameters),
    [executeService, parameters],
  );
  const authService = useMemo(
    () => new AuthService(executeService),
    [executeService],
  );
  const enumService = useMemo(
    () => new EnumService(executeService),
    [executeService],
  );

  // Handlers
  const handleFetchWithViewPerm = async () => {
    const response = await authService.fetchWithViewPerm(
      'abroadinsurance',
      formik.values.organization,
    );

    if (response && 'results' in response) {
      setWithViewPerm(response.results);
    }
  };

  const handleFetchEnums = async () => {
    const [branchesRes, countriesRes, statusChoicesRes] = await Promise.all([
      enumService.fetchAbroadInsuranceBranches(),
      enumService.fetchCountries(),
      enumService.fetchPoliciesStatusChoices(),
    ]);

    if (Array.isArray(branchesRes)) {
      setBranches(branchesRes);
    }
    if (Array.isArray(countriesRes)) {
      setCountries(countriesRes);
    }
    if (Array.isArray(statusChoicesRes)) {
      setStatusChoices(statusChoicesRes);
    }
  };

  const handleFetchData = async () => {
    const response = await abroadInsuranceService.fetchData();
    if (response && 'results' in response) {
      dispatch(tableActions.changeTableTotal(response.count));
      const results = response.results.reduce<IAbroadInsuranceDataSource[]>(
        (
          prev: IAbroadInsuranceDataSource[],
          cur: IAbroadInsuranceDataSource,
        ) => {
          return [...prev, { ...cur, key: cur.id }];
        },
        [],
      );
      setDataSource(results);
      if (additionalFileID) {
        const item = results.find((item) => item.id === additionalFileID);
        if (item) {
          setAdditionalFileID(item.id);
          setAdditionalFile(item.additional_files);
        }
      }
    }
  };

  const handleClickEye = async (id: number) => {
    dispatch(tableActions.changeTableRow(id));
    const expanded = await abroadInsuranceService.fetchExpandedRows(id);
    if (expanded && Array.isArray(expanded)) {
      setExpandedRows(expanded);
    }
  };

  const handleChangeExpired = () => setIsExpired((prev) => !prev);

  const handleOpenModal = () => setIsModalOpen(true);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    formik.setErrors({});
    formik.setTouched({});
  };

  const handleOpenEditModal = async (id: number) => {
    const selectedItem = await abroadInsuranceService.fetchItemById(id);
    if (selectedItem && 'id' in selectedItem) {
      setSelectedItem(selectedItem);
    }
  };

  const handleCloseEditModal = () => {
    setSelectedItem(undefined);
    formik.setErrors({});
    formik.setTouched({});
  };

  const handleUploadFile = (e: ChangeEvent<HTMLInputElement>) =>
    formik.setFieldValue('upload_files', e.target.files);

  const handleClickCancel = async (id: number) => {
    try {
      await abroadInsuranceService.cancelPolicy(id);
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<IAbroadInsuranceDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
    }
  };

  const handleClickDelete = async (id: number) => {
    try {
      await abroadInsuranceService.deleteItemById(id);
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<IAbroadInsuranceDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
    }
  };

  const handleClickExport = async () => {
    const response = await abroadInsuranceService.exportData();
    if (response && 'download_url' in response) {
      downloadFile(response.download_url);
    }
  };

  const handleSubmit = async (values: IAbroadInsuranceInitialValues) => {
    try {
      await abroadInsuranceService.submitForm(values, selectedItem);

      if (selectedItem && 'id' in selectedItem) {
        await abroadInsuranceService.updateStatus(
          selectedItem.id,
          values.recent_status,
          values.email_users || [],
        );
      }
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<IAbroadInsuranceDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
      dispatch(tableActions.changeTablePage({ number: 0, size: 20 }));
    }
  };

  const handleSetAdditionalFile = (id: number, file: IFile[]) => {
    setAdditionalFileID(id);
    setAdditionalFile(file);
  };

  const handleUploadAdditionalFile = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setTempAdditionalFiles(e.target.files);
    }
  };

  const handleCloseAdditionalFile = () => {
    setAdditionalFileID(undefined);
    setAdditionalFile(undefined);
    setTempAdditionalFiles(undefined);
  };

  const handleDeleteFile = async (id: number, fileId: number) => {
    await abroadInsuranceService.deleteFile(id, fileId);
    handleFetchData();
  };

  const handleSubmitFile = async () => {
    if (additionalFileID && tempAdditionalFiles) {
      await abroadInsuranceService.uploadFiles(
        additionalFileID,
        tempAdditionalFiles,
      );
      setTempAdditionalFiles(undefined);
      handleFetchData();
    }
  };

  // Columns
  const columns = getColumns(
    handleClickEye,
    handleOpenEditModal,
    handleClickDelete,
    handleClickCancel,
    handleSetAdditionalFile,
    organizations,
    branches,
    countries,
    statusChoices,
  );

  // Form Management
  const formik = useFormik<IAbroadInsuranceInitialValues>({
    initialValues: INITIAL_VALUES,
    validationSchema,
    onSubmit: handleSubmit,
  });

  // Effects
  useEffect(() => {
    handleFetchEnums();
  }, []);

  useEffect(() => {
    if (formik.values.organization) {
      handleFetchWithViewPerm();
    }
  }, [formik.values.organization]);

  useEffect(() => {
    if (selectedItem) {
      const result = prepareModalData<
        IAbroadInsuranceDataSource,
        IAbroadInsuranceInitialValues
      >(selectedItem);
      result && formik.setValues(result);
    } else {
      formik.setValues(INITIAL_VALUES);
    }
  }, [selectedItem]);

  useEffect(() => {
    handleFetchData();
  }, [isExpired, dates, search, sort.key, sort.order, page.number, page.size]);

  // JSX Return
  return (
    <Container>
      <PageTitle title="Yurtdışı Poliçeler" />
      <Flex vertical gap="middle">
        <Flex justify="space-between">
          <Flex vertical gap="small">
            <SRangePicker
              placeholder={['Başlangıç Tarihi', 'Bitiş Tarihi']}
              onChange={(_, dates: [string, string]) => setDates(dates)}
            />
            <Flex gap="small">
              <Checkbox checked={isExpired} onChange={handleChangeExpired} />
              <Typography className={globalStyles.outOfDateText}>
                Sadece Aktif Poliçeleri Göster
              </Typography>
            </Flex>
          </Flex>
          <Flex gap={5}>
            <SExportButton onClick={handleClickExport} />
            <AuthorizedButton
              model="abroadinsurance"
              action="add"
              className={styles.button}
              onClick={handleOpenModal}
            >
              <PlusOutlined className={styles.buttonIcon} />
              <div className={styles.buttonText}>Yeni Poliçe Talebi</div>
            </AuthorizedButton>
          </Flex>
        </Flex>
        <DataTable<IAbroadInsuranceDataSource>
          scrollable
          columns={columns}
          dataSource={dataSource}
          expandedRows={expandedRows}
          statusChoices={statusChoices}
        />
      </Flex>
      <AbroadInsuranceModal
        type={modalTypes.CREATE}
        open={isModalOpen}
        title="Kurumsal ~ Yurtdışı Sigortalar Poliçe Ekleme"
        subTitle="Yeni Yurtdışı Sigortalar Poliçesi Oluşturma"
        formik={formik}
        organizations={organizations}
        handleUploadFile={handleUploadFile}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseModal}
        countries={countries}
        branches={branches}
        statusChoices={statusChoices}
        withViewPerm={withViewPerm}
      />
      <AbroadInsuranceModal
        type={modalTypes.EDIT}
        open={!!selectedItem}
        title="Kurumsal ~ Yurtdışı Sigortalar Poliçe Güncelleme"
        subTitle="Yeni Sigortalar Poliçesi Güncelleme"
        formik={formik}
        organizations={organizations}
        handleUploadFile={handleUploadFile}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseEditModal}
        countries={countries}
        branches={branches}
        statusChoices={statusChoices}
        withViewPerm={withViewPerm}
      />
      <AdditionalFileModal
        open={!!additionalFileID && Array.isArray(additionalFile)}
        model="abroadinsuranceadditionalfile"
        tempAdditionalFiles={tempAdditionalFiles}
        additionalFiles={additionalFile}
        handleUploadFile={handleUploadAdditionalFile}
        handleDeleteFile={handleDeleteFile}
        handleSubmit={handleSubmitFile}
        handleCancel={handleCloseAdditionalFile}
      />
    </Container>
  );
};

export default AbroadInsurance;
